import { Tuna } from "../../../dependencies/tuna.js";
import { v4 as uuid } from "uuid";
export class EffectsChain {
    constructor(context, settings = EffectsChain.createDefaults()) {
        this._chainNodes = [];
        this.id = uuid();
        this._context = context;
        this._settings = settings;
        this._tuna = Tuna(context);
        this._preGain = this._context.createGain();
        this._preGain.gain.value = this._settings.preGain;
        this._postGain = this._context.createGain();
        this._postGain.gain.value = this._settings.postGain;
        if (this._settings.effects.length > 0) {
            let currentEffect = this.IEffectToEffectObject(this._settings.effects[0]);
            this._chainNodes.push(currentEffect);
            this._preGain.connect(currentEffect);
            for (let i = 1; i < this._settings.effects.length; i++) {
                currentEffect = this.IEffectToEffectObject(this._settings.effects[i]);
                this._chainNodes[i - 1].connect(currentEffect);
                this._chainNodes.push(currentEffect);
            }
            this._chainNodes[this._chainNodes.length - 1].connect(this._postGain);
        }
        else {
            this._preGain.connect(this._postGain);
        }
    }
    get input() {
        return this._preGain;
    }
    get effectCount() {
        return this._chainNodes.length;
    }
    get effects() {
        return this._settings.effects;
    }
    get effectNodes() {
        return this._chainNodes;
    }
    get chainName() {
        return this._settings.chainName;
    }
    set chainName(value) {
        this._settings.chainName = value;
    }
    get preGain() {
        return this._preGain.gain.value;
    }
    set preGain(value) {
        this._preGain.gain.value = value;
    }
    get postGain() {
        return this._postGain.gain.value;
    }
    set postGain(value) {
        this._preGain.gain.value = value;
    }
    destroy() {
        this._preGain.disconnect();
        for (let i = 0; i < this._chainNodes.length; i++) {
            this._chainNodes[i].disconnect();
        }
        this._postGain.disconnect();
    }
    connect(node) {
        if (node instanceof AudioNode) {
            this._postGain.connect(node);
        }
        else {
            this._postGain.connect(node.input);
        }
    }
    disconnect(node) {
        if (node instanceof AudioNode) {
            this._postGain.disconnect(node);
        }
        else {
            this._postGain.disconnect(node.input);
        }
    }
    disconnectAll() {
        this._postGain.disconnect();
    }
    addEffect(index, effect) {
        if (index < 0 || index > this._chainNodes.length) {
            throw new RangeError(("Index out of range:" + index.toString()));
        }
        let effectObject = this.IEffectToEffectObject(effect);
        this._connectEffect(index, effectObject);
        this._settings.effects.splice(index, 0, effect);
    }
    removeEffect(index) {
        if (index >= 0 && index < this._chainNodes.length) {
            this._disconnectEffect(index);
            this._settings.effects.splice(index, 1);
        }
        else {
            throw new RangeError("Index out of range");
        }
    }
    moveEffect(index, newIndex) {
        index = Math.max(Math.min(this.effectCount, index), 0);
        newIndex = Math.max(Math.min(this.effectCount, newIndex), 0);
        if (index != newIndex) {
            let effect = this._disconnectEffect(index);
            this._connectEffect(newIndex, effect);
            let temp = this._settings.effects[index];
            this._settings.effects.splice(index, 1);
            this._settings.effects.splice(newIndex, 0, temp);
        }
    }
    getDelayTime() {
        let longestDelay = 0;
        this._settings.effects.forEach((effect) => {
            let thisEffectDelay = 0;
            if (effect.effectType == "Delay") {
                let feedback = effect.properties.find(property => property.propertyName == "feedback").value;
                let delayTime = effect.properties.find(property => property.propertyName == "delayTime").value / 1000;
                thisEffectDelay = Math.log(0.005) / Math.log(feedback) * delayTime;
            }
            else if (effect.effectType == "PingPongDelay") {
                let feedback = effect.properties.find(property => property.propertyName == "feedback").value;
                let l = effect.properties.find(property => property.propertyName == "delayTimeLeft").value / 1000;
                let r = effect.properties.find(property => property.propertyName == "delayTimeRight").value / 1000;
                thisEffectDelay = Math.log(0.005) / Math.log(feedback) * Math.max(l + r);
            }
            else if (effect.effectType == "Convolver") {
                thisEffectDelay = 3;
            }
            longestDelay += thisEffectDelay;
        });
        return longestDelay;
    }
    serialise() {
        return this._settings;
    }
    static createDefaults() {
        return {
            "chainName": "",
            "effects": [],
            "preGain": 1,
            "postGain": 1,
            "connections": ["Context"]
        };
    }
    IEffectToEffectObject(effect) {
        let p = this._convertPropertyToObject(effect.properties);
        switch (effect.effectType) {
            case "Chorus":
                return new this._tuna.Chorus(p);
            case "Delay":
                return new this._tuna.Delay(p);
            case "Overdrive":
                return new this._tuna.Overdrive(p);
            case "Compressor":
                return new this._tuna.Compressor(p);
            case "Convolver":
                return new this._tuna.Convolver(p);
            case "Filter":
                return new this._tuna.Filter(p);
            case "Cabinet":
                return new this._tuna.Cabinet(p);
            case "Tremolo":
                return new this._tuna.Tremolo(p);
            case "WahWah":
                return new this._tuna.WahWah(p);
            case "Phaser":
                return new this._tuna.Phaser(p);
            case "Bitcrusher":
                return new this._tuna.Bitcrusher(p);
            case "Moog":
                return new this._tuna.MoogFilter(p);
            case "PingPongDelay":
                return new this._tuna.PingPongDelay(p);
            case "Panner":
                return new this._tuna.Panner(p);
            case "Gain":
                return new this._tuna.Gain(p);
            default:
                throw new Error("Invalid effect name.");
        }
    }
    _convertPropertyToObject(properties) {
        let newObj = {};
        properties.forEach((prop) => {
            newObj[prop.propertyName] = prop.value;
        });
        return newObj;
    }
    _connectEffect(index, effect) {
        if (index < 0 || index > this._chainNodes.length) {
            throw new Error("Invalid index to disconnect effect.");
        }
        if (index > 0) {
            this._chainNodes[index - 1].disconnect();
            this._chainNodes[index - 1].connect(effect);
        }
        else {
            this._preGain.disconnect();
            this._preGain.connect(effect);
        }
        if (index === this._chainNodes.length) {
            effect.connect(this._postGain);
            this._chainNodes.push(effect);
        }
        else {
            effect.connect(this._chainNodes[index]);
            this._chainNodes.splice(index, 0, effect);
        }
    }
    _disconnectEffect(index) {
        if (index < 0 || index >= this._chainNodes.length || this._chainNodes.length === 0) {
            throw new Error("Invalid index to disconnect effect.");
        }
        this._chainNodes[index].disconnect();
        let node = null;
        if (index > 0) {
            node = this._chainNodes[index - 1];
        }
        else {
            node = this._preGain;
        }
        node.disconnect();
        if (index === this._chainNodes.length - 1) {
            node.connect(this._postGain);
        }
        else {
            node.connect(this._chainNodes[index + 1]);
        }
        return this._chainNodes.splice(index, 1)[0];
    }
    static Chorus() {
        return {
            id: uuid(),
            effectType: "Chorus",
            properties: [
                { type: "number", propertyName: "rate", value: 1.5, editable: true, step: 0.01, min: 0.01, max: 8 },
                { type: "number", propertyName: "feedback", value: 0.4, editable: true, step: 0.01, min: 0, max: 1 },
                { type: "number", propertyName: "depth", value: 0.7, editable: true, step: 0.01, min: 0, max: 1 },
                { type: "number", propertyName: "delay", value: 0.0045, editable: true, step: 0.01, min: 0, max: 1 },
                { type: "boolean", propertyName: "bypass", value: false, editable: true }
            ]
        };
    }
    static Delay() {
        return {
            id: uuid(),
            effectType: "Delay",
            properties: [
                { type: "number", propertyName: "feedback", value: 0.45, editable: true, step: 0.01, min: 0, max: 0.999 },
                { type: "number", displayName: "Delay Time", propertyName: "delayTime", value: 100, editable: true, step: 1, min: 1, max: 10000 },
                { type: "number", displayName: "Wet Level", propertyName: "wetLevel", value: 0.5, editable: true, step: 0.01, min: 0, max: 1 },
                { type: "number", displayName: "Dry Level", propertyName: "dryLevel", value: 1, editable: true, step: 0.01, min: 0, max: 1 },
                { type: "number", propertyName: "cutoff", value: 20000, editable: true, step: 1, min: 20, max: 22050 },
                { type: "boolean", propertyName: "bypass", value: false, editable: true }
            ]
        };
    }
    static Phaser() {
        return {
            id: uuid(),
            effectType: "Phaser",
            properties: [
                { type: "number", propertyName: "rate", value: 0.1, editable: true, step: 0.01, min: 0, max: 8 },
                { type: "number", propertyName: "depth", value: 0.6, editable: true, step: 0.01, min: 0, max: 1 },
                { type: "number", propertyName: "feedback", value: 0.7, editable: true, step: 0.01, min: 0, max: 0.999 },
                { type: "number", displayName: "Stereo Phase", propertyName: "stereoPhase", value: 40, editable: true, step: 1, min: 0, max: 180 },
                { type: "number", displayName: "Base Modulation Frequency", propertyName: "baseModulationFrequency", value: 700, editable: true, step: 1, min: 500, max: 1500 },
                { type: "boolean", propertyName: "bypass", value: false, editable: true }
            ]
        };
    }
    static Overdrive() {
        return {
            id: uuid(),
            effectType: "Overdrive",
            properties: [
                { type: "number", displayName: "Output Gain", propertyName: "outputGain", value: 0, editable: true, step: 1, min: -42, max: 0 },
                { type: "number", propertyName: "drive", value: 1, editable: true, step: 0.01, min: 0, max: 1 },
                { type: "number", displayName: "Curve Amount", propertyName: "curveAmount", value: 0.725, editable: true, step: 0.01, min: 0, max: 0.99 },
                { type: "number", displayName: "Algorithm Index", propertyName: "algorithmIndex", value: 0, editable: true, step: 1, min: 0, max: 5 },
                { type: "boolean", propertyName: "bypass", value: false, editable: true }
            ]
        };
    }
    static Compressor() {
        return {
            id: uuid(),
            effectType: "Compressor",
            properties: [
                { type: "number", propertyName: "threshold", value: -20, editable: true, step: 1, min: -100, max: 0 },
                { type: "number", displayName: "Makeup Gain", propertyName: "makeupGain", value: 1, editable: true, step: 1, min: 0, max: 200 },
                { type: "number", propertyName: "attack", value: 1, editable: true, step: 1, min: 0, max: 1000 },
                { type: "number", propertyName: "release", value: 250, editable: true, step: 1, min: 0, max: 3000 },
                { type: "number", propertyName: "ratio", value: 4, editable: true, step: 1, min: 1, max: 20 },
                { type: "number", propertyName: "knee", value: 5, editable: true, step: 1, min: 0, max: 40 },
                { type: "boolean", displayName: "Auto Makeup", propertyName: "automakeup", value: false, editable: true },
                { type: "boolean", propertyName: "bypass", value: false, editable: true }
            ]
        };
    }
    static Convolver() {
        return {
            id: uuid(),
            effectType: "Convolver",
            properties: [
                { type: "number", displayName: "High Cut", propertyName: "highCut", value: 22050, editable: true, step: 1, min: 20, max: 22050 },
                { type: "number", displayName: "Low Cut", propertyName: "lowCut", value: 20, editable: true, step: 1, min: 20, max: 22050 },
                { type: "number", displayName: "Wet Level", propertyName: "wetLevel", value: 1, editable: true, step: 0.01, min: 0, max: 1 },
                { type: "number", displayName: "Dry Level", propertyName: "dryLevel", value: 1, editable: true, step: 0.01, min: 0, max: 1 },
                { type: "number", propertyName: "level", value: 0.7, editable: true, step: 0.01, min: 0, max: 1 },
                { type: "string", propertyName: "impulse", value: "/dependencies/impulses/impulse_rev.wav", editable: false },
                { type: "boolean", propertyName: "bypass", value: false, editable: true }
            ]
        };
    }
    static Filter() {
        return {
            id: uuid(),
            effectType: "Filter",
            properties: [
                { type: "number", propertyName: "frequency", value: 22050, editable: true, step: 1, min: 20, max: 22050 },
                { type: "number", propertyName: "Q", value: 1, editable: true, step: 0.001, min: 0.001, max: 100 },
                { type: "number", propertyName: "gain", value: 0, editable: true, step: 1, min: -40, max: 40 },
                { type: "list", displayName: "Filter Type", propertyName: "filterType", value: "lowpass", editable: true, options: ["lowpass", "highpass", "bandpass", "lowshelf", "highshelf", "peaking", "notch", "allpass"] },
                { type: "boolean", propertyName: "bypass", value: false, editable: true }
            ]
        };
    }
    static Cabinet() {
        return {
            id: uuid(),
            effectType: "Cabinet",
            properties: [
                { type: "number", displayName: "Makeup Gain", propertyName: "makeupGain", value: 1, editable: true, step: 0.1, min: 0, max: 20 },
                { type: "string", displayName: "Impulse Path", propertyName: "impulsePath", value: "/dependencies/impulses/impulse_guitar.wav", editable: false },
                { type: "boolean", propertyName: "bypass", value: false, editable: true }
            ]
        };
    }
    static Tremolo() {
        return {
            id: uuid(),
            effectType: "Tremolo",
            properties: [
                { type: "number", propertyName: "intensity", value: 1, editable: true, step: 0.01, min: 0, max: 1 },
                { type: "number", propertyName: "rate", value: 5, editable: true, step: 0.001, min: 0.001, max: 8 },
                { type: "number", propertyName: "stereoPhase", value: 0, editable: true, step: 1, min: 0, max: 180 },
                { type: "boolean", propertyName: "bypass", value: false, editable: true }
            ]
        };
    }
    static WahWah() {
        return {
            id: uuid(),
            effectType: "WahWah",
            properties: [
                { type: "boolean", displayName: "Auto", propertyName: "automode", value: true, editable: true },
                { type: "number", displayName: "Base Frequency", propertyName: "baseFrequency", value: 0.5, editable: true, step: 0.01, min: 0, max: 1 },
                { type: "number", displayName: "Excursion Octaves", propertyName: "excursionOctaves", value: 2, editable: true, step: 1, min: 1, max: 6 },
                { type: "number", propertyName: "sweep", value: 0.2, editable: true, step: 0.01, min: 0, max: 1 },
                { type: "number", propertyName: "resonance", value: 10, editable: true, step: 1, min: 1, max: 100 },
                { type: "number", propertyName: "sensitivity", value: 0.5, editable: true, step: 0.01, min: -1, max: 1 },
                { type: "boolean", propertyName: "bypass", value: false, editable: true }
            ]
        };
    }
    static Bitcrusher() {
        return {
            id: uuid(),
            effectType: "Bitcrusher",
            properties: [
                { type: "number", propertyName: "bits", value: 4, editable: true, step: 1, min: 1, max: 16 },
                { type: "number", displayName: "Normal Frequency", propertyName: "normFreq", value: 0.1, editable: true, step: 0.01, min: 0, max: 1 },
                { type: "number", displayName: "Buffer Size", propertyName: "bufferSize", value: 4096, editable: true, step: 1, min: 256, max: 16384 },
            ]
        };
    }
    static Moog() {
        return {
            id: uuid(),
            effectType: "Moog",
            properties: [
                { type: "number", propertyName: "cutoff", value: 0.065, editable: true, step: 0.001, min: 0, max: 1 },
                { type: "number", propertyName: "resonance", value: 3.5, editable: true, step: 0.01, min: 0, max: 4 },
                { type: "number", displayName: "Buffer Size", propertyName: "bufferSize", value: 4096, editable: true, step: 1, min: 256, max: 16384 },
            ]
        };
    }
    static PingPongDelay() {
        return {
            id: uuid(),
            effectType: "PingPongDelay",
            properties: [
                { type: "number", propertyName: "wetLevel", value: 1.5, editable: true, step: 0.01, min: 0.01, max: 8 },
                { type: "number", propertyName: "feedback", value: 0.4, editable: true, step: 0.01, min: 0, max: 1 },
                { type: "number", displayName: "Delay Time (Left)", propertyName: "delayTimeLeft", value: 200, editable: true, step: 1, min: 1, max: 10000 },
                { type: "number", displayName: "Delay Time (Right)", propertyName: "delayTimeRight", value: 400, editable: true, step: 1, min: 1, max: 10000 },
            ]
        };
    }
    static Panner() {
        return {
            id: uuid(),
            effectType: "Panner",
            properties: [{ type: "number", propertyName: "pan", value: 0, editable: true, step: 0.01, min: -1, max: 1 }]
        };
    }
    static Gain() {
        return {
            id: uuid(),
            effectType: "Gain",
            properties: [{ type: "number", propertyName: "gain", value: 1, editable: true, step: 0.01, min: 0, max: 2 }]
        };
    }
}
EffectsChain.possibleEffects = new Map([["Chorus", EffectsChain.Chorus],
    ["Delay", EffectsChain.Delay],
    ["Ping Pong Delay", EffectsChain.PingPongDelay],
    ["Overdrive", EffectsChain.Overdrive],
    ["Gain", EffectsChain.Gain],
    ["Compressor", EffectsChain.Compressor],
    ["Pan", EffectsChain.Panner],
    ["Convolver Reverb", EffectsChain.Convolver],
    ["Tremolo", EffectsChain.Tremolo],
    ["Filter", EffectsChain.Filter],
    ["Bitcrusher", EffectsChain.Bitcrusher],
    ["Phaser", EffectsChain.Phaser],
    ["Wah", EffectsChain.WahWah],
    ["Moog Filter", EffectsChain.Moog],
    ["Cabinet Emulator", EffectsChain.Cabinet]]);
//# sourceMappingURL=EffectsChain.js.map